const CARD_SLIDER_SELECTOR = '.js-card-full-slider';
const CARD_SLIDER_MOUNT_MARGIN = '200px';
const CARD_SLIDER_DESTROY_MARGIN = '800px';
const CARD_SLIDER_REVEAL_FALLBACK_MS = 600;

const cardProductCache = new Map();

function normalizeImageSrc(src) {
  return src.split('?')[0].replace(/^https?:/, '');
}

function loadCardProduct(handle) {
  if (!cardProductCache.has(handle)) {
    const promise = fetch(`/products/${handle}.js`)
      .then((res) => {
        if (!res.ok) throw new Error(`product fetch failed: ${res.status}`);
        return res.json();
      })
      .then((product) => {
        const images = product.images.map((src) => ({src}));
        const mediaIndexMap = {};
        product.variants.forEach((v) => {
          if (!v.featured_image) return;
          const idx = images.findIndex(
            (img) => normalizeImageSrc(img.src) === normalizeImageSrc(v.featured_image.src)
          );
          if (idx !== -1) mediaIndexMap[v.id] = idx;
        });
        return {images, mediaIndexMap};
      })
      .catch((err) => {
        cardProductCache.delete(handle);
        throw err;
      });
    cardProductCache.set(handle, promise);
  }
  return cardProductCache.get(handle);
}

async function ensureSlider(card) {
  if (card._swiperInstance) return card._swiperInstance;
  if (card._sliderPromise) return card._sliderPromise;

  const token = (card._mountToken = (card._mountToken || 0) + 1);

  card._sliderPromise = loadCardProduct(card.dataset.productHandle)
    .then((data) => {
      if (token !== card._mountToken || !card.isConnected) return null;
      card._mediaIndexMap = data.mediaIndexMap;
      card._swiperInstance = buildSlider(card, data.images);
      return card._swiperInstance;
    })
    .catch(() => null)
    .finally(() => {
      if (token === card._mountToken) card._sliderPromise = null;
    });

  return card._sliderPromise;
}

function getStaticImage(card) {
  return card.querySelector('img:not(.card-full-slider img)');
}

function destroySlider(card) {
  card._mountToken = (card._mountToken || 0) + 1;
  card._sliderPromise = null;

  const swiper = card._swiperInstance;
  if (!swiper) return;

  card._lastIndex = swiper.activeIndex;
  const swiperEl = swiper.el;
  swiper.destroy(true, true);
  swiperEl.remove();
  card._swiperInstance = null;

  const staticImg = getStaticImage(card);
  if (staticImg) staticImg.style.display = '';
}

function findAssociatedSlider(fromEl) {
  let el = fromEl.parentElement;
  while (el) {
    const slider = el.querySelector(CARD_SLIDER_SELECTOR);
    if (slider) return slider;
    el = el.parentElement;
  }
  return null;
}

function buildSlider(card, images) {
  // 複製ノード等に残った未初期化の .card-full-slider を除去
  card.querySelectorAll(':scope > .card-full-slider').forEach((el) => {
    if (!el.swiper) el.remove();
  });
  const staticImg = getStaticImage(card);
  if (staticImg) staticImg.style.display = '';

  const swiperEl = document.createElement('div');
  swiperEl.className = 'swiper card-full-slider';
  swiperEl.innerHTML = '<div class="swiper-wrapper"></div>';
  card.appendChild(swiperEl);

  return new Swiper(swiperEl, {
    slidesPerView: 1,
    spaceBetween: 0,
    speed: 350,
    touchRatio: 0.7,
    preventClicks: true,
    preventClicksPropagation: true,
    nested: true,
    initialSlide: card._lastIndex || 0,
    virtual: {
      slides: images.map(
        (it, i) =>
          `<img src="${it.src}&width=600" loading="lazy" decoding="async"${i === 0 ? ' fetchpriority="high"' : ''}>`
      ),
      cache: true,
    },
    on: {
      init(sw) {
        const firstImg =
          sw.el.querySelector('.swiper-slide-active img') || sw.slides[0]?.querySelector('img');

        const hideStatic = () => {
          if (sw.destroyed) return;
          const img = getStaticImage(card);
          if (img) img.style.display = 'none';
        };

        const reveal = () => {
          // 同一フレームでのクラス付与だと transition がスキップされるため 2フレーム待つ
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (sw.destroyed) return;
              swiperEl.classList.add('is-ready');
              let done = false;
              const finish = () => {
                if (done) return;
                done = true;
                hideStatic();
              };
              swiperEl.addEventListener('transitionend', finish, {once: true});
              setTimeout(finish, CARD_SLIDER_REVEAL_FALLBACK_MS);
            });
          });
        };

        if (firstImg && !firstImg.complete) {
          firstImg.addEventListener('load', reveal, {once: true});
        } else {
          reveal();
        }
      },
      touchStart(sw) {
        sw._startedAtEnd = sw.isEnd;
        sw._startedAtBeginning = sw.isBeginning;
      },
      touchEnd(sw) {
        const diff = sw.touches.currentX - sw.touches.startX;
        const parentSwiper = card.closest('.swiper')?.swiper;
        if (!parentSwiper) return;
        if (sw._startedAtEnd && diff < -30) {
          parentSwiper.slideNext();
        } else if (sw._startedAtBeginning && diff > 30) {
          parentSwiper.slidePrev();
        }
      },
    },
  });
}

/* ---------- mount / destroy ---------- */

const cardSliderMountObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      entry.target._inMountRange = entry.isIntersecting;
      if (entry.isIntersecting) ensureSlider(entry.target);
    });
  },
  {rootMargin: CARD_SLIDER_MOUNT_MARGIN}
);

const cardSliderDestroyObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) destroySlider(entry.target);
    });
  },
  {rootMargin: CARD_SLIDER_DESTROY_MARGIN}
);

const observedCardSliders = new WeakSet();

function observeCardSlider(card) {
  if (observedCardSliders.has(card)) return;
  observedCardSliders.add(card);
  cardSliderMountObserver.observe(card);
  cardSliderDestroyObserver.observe(card);
}

function releaseCardSlider(card) {
  if (card.isConnected) return;
  destroySlider(card);
  cardSliderMountObserver.unobserve(card);
  cardSliderDestroyObserver.unobserve(card);
  observedCardSliders.delete(card);
}

function scanCardSliders(root) {
  if (root.matches?.(CARD_SLIDER_SELECTOR)) observeCardSlider(root);
  root.querySelectorAll?.(CARD_SLIDER_SELECTOR).forEach(observeCardSlider);
}

scanCardSliders(document);

// morph でカード要素が再利用された場合の作り直し
function resetCardSlider(card, {keepIndex}) {
  destroySlider(card);
  if (!keepIndex) {
    card._lastIndex = 0;
    card._mediaIndexMap = null;
  }
  if (card._inMountRange) ensureSlider(card);
}

// 絞り込み・並び替え等（section-renderer の morph）による更新の追従
new MutationObserver((mutations) => {
  const resetTargets = new Map();

  mutations.forEach((m) => {
    if (m.type === 'attributes') {
      // 再利用されたカードの商品が入れ替わった
      if (m.target.matches(CARD_SLIDER_SELECTOR) && m.target.dataset.productHandle !== m.oldValue) {
        resetTargets.set(m.target, false);
      }
      return;
    }

    m.addedNodes.forEach((node) => {
      if (node.nodeType === 1) scanCardSliders(node);
    });

    m.removedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;

      // カードは残ったまま、追加した .card-full-slider だけ morph で消された
      if (node.classList.contains('card-full-slider')) {
        const card = m.target.closest(CARD_SLIDER_SELECTOR);
        if (card && card.isConnected && card._swiperInstance?.el === node && !resetTargets.has(card)) {
          resetTargets.set(card, true);
        }
        return;
      }

      if (node.matches(CARD_SLIDER_SELECTOR)) releaseCardSlider(node);
      node.querySelectorAll(CARD_SLIDER_SELECTOR).forEach(releaseCardSlider);
    });
  });

  resetTargets.forEach((keepIndex, card) => {
    if (card.isConnected) resetCardSlider(card, {keepIndex});
  });
}).observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeOldValue: true,
  attributeFilter: ['data-product-handle'],
});

/* ---------- swatch ---------- */

document.addEventListener('click', async (e) => {
  const swatchInput = e.target.closest('[data-option-media-id]');
  if (!swatchInput) return;
  const card = findAssociatedSlider(swatchInput);
  if (!card) return;

  const swiper = await ensureSlider(card);
  if (!swiper || swiper.destroyed) return;

  const index = card._mediaIndexMap?.[Number(swatchInput.dataset.variantId)];
  if (index !== undefined) swiper.slideTo(index);
});